import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  Link,
  Tooltip,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CodeIcon from '@mui/icons-material/Tag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import apiClient from '@uniqcamp/shared/services/api.service';
import { UniqCampLogo, isValidEmail, isValidMobileDigits, mobileDigitsOnly, normalizeEmail } from '@uniqcamp/shared';
import { useToast } from '../contexts/ToastContext';

const UNIQUE_CODE_LENGTH = 6;
const CODE_CHECK_DEBOUNCE_MS = 400;

type CodeCheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

const RegisterSchool: React.FC = () => {
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uniqueCode, setUniqueCode] = useState('');
  const [codeCheckStatus, setCodeCheckStatus] = useState<CodeCheckStatus>('idle');
  const [name, setName] = useState('');
  const [registeredMobile, setRegisteredMobile] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [address, setAddress] = useState({ street: '', city: '', state: '', zipCode: '', country: '' });
  const [logo, setLogo] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');
  const [onboarded, setOnboarded] = useState(false);
  const { showToast } = useToast();

  const normalizedCode = uniqueCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, UNIQUE_CODE_LENGTH);
  const trimmedUniqueCode = uniqueCode.trim();
  const isCodeValidLength = normalizedCode.length === UNIQUE_CODE_LENGTH;

  const checkCode = useCallback(async (code: string) => {
    if (code.length !== UNIQUE_CODE_LENGTH || !/^[A-Z0-9]{6}$/.test(code)) {
      setCodeCheckStatus('invalid');
      return;
    }
    setCodeCheckStatus('checking');
    try {
      const res = await apiClient.get(`/schools/check-code?code=${encodeURIComponent(code)}`);
      const data = res.data as { available?: boolean; message?: string };
      setCodeCheckStatus(data.available ? 'available' : 'taken');
    } catch {
      setCodeCheckStatus('idle');
    }
  }, []);

  useEffect(() => {
    if (!isCodeValidLength) {
      setCodeCheckStatus(trimmedUniqueCode.length > 0 ? 'invalid' : 'idle');
      return;
    }
    const timer = setTimeout(() => checkCode(normalizedCode), CODE_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [normalizedCode, isCodeValidLength, trimmedUniqueCode, checkCode]);

  const handleUniqueCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, UNIQUE_CODE_LENGTH);
    setUniqueCode(v);
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 15);
    setRegisteredMobile(v);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegisteredEmail(e.target.value);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogo((reader.result as string) || '');
    reader.readAsDataURL(file);
  };

  const canSubmitForm =
    name.trim().length > 0 &&
    codeCheckStatus === 'available' &&
    isValidMobileDigits(registeredMobile) &&
    isValidEmail(registeredEmail);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiClient.post('/schools/register', {
        name: name.trim(),
        uniqueCode: normalizedCode,
        registeredMobile: mobileDigitsOnly(registeredMobile),
        registeredEmail: normalizeEmail(registeredEmail),
        address: Object.fromEntries(
          Object.entries(address).filter(([, v]) => v != null && String(v).trim() !== '')
        ) || undefined,
        logo: logo || undefined,
      });
      const data = res.data as { success?: boolean; school?: { uniqueCode?: string } };
      if (data.success) {
        setUniqueCode(data.school?.uniqueCode || normalizedCode);
        setStep('verify');
        showToast('success', 'OTP sent to your mobile and email. Enter the codes below.');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      const msg = e.response?.data?.message ?? 'Registration failed. Please try again.';
      setError(msg);
      showToast('error', msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (adminPassword !== adminPasswordConfirm) {
      setError('Passwords do not match');
      showToast('error', 'Passwords do not match');
      return;
    }
    if (adminPassword.length < 6) {
      setError('Password must be at least 6 characters');
      showToast('error', 'Password must be at least 6 characters');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await apiClient.post('/schools/register/verify', {
        uniqueCode: uniqueCode.trim().toUpperCase(),
        mobileOtp: mobileOtp.trim(),
        emailOtp: emailOtp.trim(),
        adminPassword,
      });
      const data = res.data as { success?: boolean };
      if (data.success) {
        setOnboarded(true);
        showToast('success', 'School onboarding complete. Log in with your school code, email and password.');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      const msg = e.response?.data?.message ?? 'Verification failed. Please check the OTPs.';
      setError(msg);
      showToast('error', msg);
    } finally {
      setLoading(false);
    }
  };

  if (onboarded) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 440, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Onboarding complete
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Your school is registered. Contact your super admin to get staff accounts, or log in once accounts are created.
          </Typography>
          <Button component={RouterLink} to="/login" variant="contained" fullWidth>
            Go to login
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          background: 'linear-gradient(135deg, #4f227d 0%, #371755 50%, #2d1147 100%)',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Box sx={{ color: 'white', textAlign: 'center', maxWidth: 360 }}>
          <SchoolIcon sx={{ fontSize: 64, mb: 2, opacity: 0.95 }} />
          <Box component="h1" sx={{ m: 0, display: 'flex', justifyContent: 'center', mb: 1 }}>
            <UniqCampLogo style={{ maxWidth: 280, maxHeight: 80, width: 'auto' }} />
          </Box>
          <Typography variant="h6" sx={{ mt: 2, opacity: 0.9 }}>
            School onboarding
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            Register your school to get started. We’ll verify your mobile and email with an OTP.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch', minHeight: 0 }}>
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3.5 }, width: '100%', height: '100%', overflow: 'auto', borderRadius: 0 }}>
          {step === 'form' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Step 1 of 2
            </Typography>
          )}
          {step === 'verify' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Step 2 of 2
            </Typography>
          )}
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {step === 'form' ? 'Register your school' : 'Verify your contact'}
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Divider sx={{ my: 2.5 }} />
          {step === 'form' ? (
            <form onSubmit={handleRegister}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                School details
              </Typography>
              <TextField
                label="School name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                size="small"
                margin="normal"
                placeholder="e.g. Sunshine Public School"
                InputProps={{ startAdornment: <InputAdornment position="start"><SchoolIcon fontSize="small" color="action" /></InputAdornment> }}
              />
              <TextField
                label="School code"
                value={uniqueCode}
                onChange={handleUniqueCodeChange}
                required
                fullWidth
                size="small"
                margin="normal"
                placeholder="e.g. ABC123"
                inputProps={{ maxLength: UNIQUE_CODE_LENGTH }}
                helperText={`${normalizedCode.length}/${UNIQUE_CODE_LENGTH} — Letters or numbers only. Cannot be changed later.`}
                error={codeCheckStatus === 'taken' || codeCheckStatus === 'invalid'}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CodeIcon fontSize="small" color="action" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      {codeCheckStatus === 'checking' && <CircularProgress size={22} />}
                      {codeCheckStatus === 'available' && (
                        <Tooltip title="Code available" arrow>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </Tooltip>
                      )}
                      {codeCheckStatus === 'taken' && (
                        <Tooltip title="This code is already registered" arrow>
                          <CancelIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                      {codeCheckStatus === 'invalid' && normalizedCode.length > 0 && (
                        <Tooltip title={normalizedCode.length < UNIQUE_CODE_LENGTH ? 'Code must be exactly 6 characters' : 'Use only letters and numbers'} arrow>
                          <CancelIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ my: 2.5 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Contact (we’ll send OTP here)
              </Typography>
              <TextField
                label="Mobile number"
                value={registeredMobile}
                onChange={handleMobileChange}
                required
                fullWidth
                size="small"
                margin="normal"
                placeholder="10–15 digits, e.g. 9876543210"
                inputMode="tel"
                inputProps={{ maxLength: 15 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment> }}
              />
              <TextField
                label="Email address"
                type="email"
                value={registeredEmail}
                onChange={handleEmailChange}
                required
                fullWidth
                size="small"
                margin="normal"
                placeholder="school@example.com"
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" color="action" /></InputAdornment> }}
              />

              <Divider sx={{ my: 2.5 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Address 
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <TextField size="small" fullWidth label="Street" value={address.street} onChange={(e) => setAddress((a) => ({ ...a, street: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" fullWidth label="City" value={address.city} onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" fullWidth label="State" value={address.state} onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField size="small" fullWidth label="Zip" value={address.zipCode} onChange={(e) => setAddress((a) => ({ ...a, zipCode: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField size="small" fullWidth label="Country" value={address.country} onChange={(e) => setAddress((a) => ({ ...a, country: e.target.value }))} />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                School logo 
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<AddPhotoAlternateIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {logo ? 'Change logo' : 'Upload logo'}
                  <input type="file" accept="image/*" onChange={handleLogoChange} hidden />
                </Button>
                {logo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="img" src={logo} alt="Logo" sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
                    <Button size="small" color="inherit" onClick={() => setLogo('')}>Remove</Button>
                  </Box>
                )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !canSubmitForm}
                sx={{ mt: 1, py: 1.5 }}
              >
                {loading ? 'Sending OTP…' : 'Continue — Send OTP'}
              </Button>
              {!canSubmitForm && codeCheckStatus !== 'available' && normalizedCode.length === UNIQUE_CODE_LENGTH && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
                  Use an available school code (green tick) to continue.
                </Typography>
              )}
            </form>
          ) : (
            <form onSubmit={handleVerify}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We sent a 6-digit code to your mobile and email. Enter it below to verify.
              </Typography>
              <TextField
                label="Code from mobile"
                value={mobileOtp}
                onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                fullWidth
                size="small"
                margin="normal"
                placeholder="6-digit code"
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              />
              <TextField
                label="Code from email"
                value={emailOtp}
                onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                fullWidth
                size="small"
                margin="normal"
                placeholder="6-digit code"
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 0.5 }}>
                Set your login password (you will use this with your email to sign in)
              </Typography>
              <TextField
                label="Password"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                required
                fullWidth
                size="small"
                margin="normal"
                placeholder="At least 6 characters"
                inputProps={{ minLength: 6 }}
                helperText="Min 6 characters"
              />
              <TextField
                label="Confirm password"
                type="password"
                value={adminPasswordConfirm}
                onChange={(e) => setAdminPasswordConfirm(e.target.value)}
                required
                fullWidth
                size="small"
                margin="normal"
                placeholder="Repeat password"
                error={adminPasswordConfirm.length > 0 && adminPassword !== adminPasswordConfirm}
                helperText={adminPasswordConfirm.length > 0 && adminPassword !== adminPasswordConfirm ? 'Passwords do not match' : undefined}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || adminPassword.length < 6 || adminPassword !== adminPasswordConfirm}
                sx={{ mt: 2, py: 1.5 }}
              >
                {loading ? 'Verifying…' : 'Verify & finish'}
              </Button>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  size="medium"
                  disabled={loading}
                  onClick={async () => {
                    setError('');
                    setLoading(true);
                    try {
                      const res = await apiClient.post('/schools/register/resend-otp', { uniqueCode: uniqueCode.trim().toUpperCase() });
                      const data = res.data as { success?: boolean; message?: string };
                      if (data.success) showToast('success', data.message ?? 'OTP sent again.');
                    } catch (err: unknown) {
                      const e = err as { response?: { data?: { message?: string } } };
                      const msg = e.response?.data?.message ?? 'Failed to resend OTP.';
                      setError(msg);
                      showToast('error', msg);
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Resend OTP
                </Button>
                <Button type="button" fullWidth variant="text" size="medium" onClick={() => setStep('form')}>
                  Back to details
                </Button>
              </Box>
            </form>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Already have an account? <Link component={RouterLink} to="/login" underline="hover">Log in</Link>
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default RegisterSchool;
